import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatUSD, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

export default function TransferFunds() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "MASTER_ADMIN";
  const preselectAgentId = params.get("agent") || "";
  const [agents, setAgents] = useState([]);
  const [masters, setMasters] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [balance, setBalance] = useState(null);
  const [agentId, setAgentId] = useState(preselectAgentId);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const calls = [
      // First 500 agents in the picker; admin typically deep-links from My Agents.
      api.get("/agents?limit=500"),
      api.get("/transfers"),
      api.get("/wallet"),
    ];
    if (isAdmin) calls.push(api.get("/master-agents"));
    const results = await Promise.all(calls);
    const agentsResp = results[0].data;
    setAgents(Array.isArray(agentsResp) ? agentsResp : (agentsResp.items || []));
    setTransfers(results[1].data);
    setBalance(results[2].data.balance_usd);
    if (isAdmin && results[3]) setMasters(results[3].data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [isAdmin]);

  // Keep selection in sync when the URL changes (e.g. coming from My Agents).
  useEffect(() => {
    if (preselectAgentId) setAgentId(preselectAgentId);
  }, [preselectAgentId]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!agentId || !amt || amt <= 0) {
      toast.error("Pick an agent and enter a positive amount");
      return;
    }
    setBusy(true);
    try {
      await api.post("/transfers", { agent_id: agentId, amount: amt, note: note || null });
      toast.success("Funds transferred");
      setAmount("");
      setNote("");
      setAgentId("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="transfer-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("nav.transfer_funds")}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("nav.transfer_funds")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t("transfer.subtitle", "Move funds between your master wallet and your agents.")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-gradient-to-br from-[#0F62FE] to-[#0043CE] rounded-xl shadow-sm p-6 text-white">
          <div className="text-xs uppercase tracking-widest text-white/70">{t("transfer.master_balance", "Master balance")}</div>
          <div
            className="text-4xl font-bold mt-2"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {balance == null ? "—" : formatUSD(balance)}
          </div>
          <div className="text-white/70 text-xs mt-1">{t("transfer.available_to_transfer", "Available to transfer")}</div>
        </div>

        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("role.AGENT")}
            </label>
            <select
              data-testid="transfer-agent-select"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className={inputCls}
              required
            >
              <option value="">{t("transfer.select_recipient", "Select recipient")}</option>
              {isAdmin && masters.length > 0 && (
                <optgroup label={t("role.MASTER_AGENT")}>
                  {masters.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.username} — {m.name} ({formatUSD(m.balance_usd)})
                    </option>
                  ))}
                </optgroup>
              )}
              {isAdmin ? (
                <optgroup label={t("nav.agents")}>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.username} — {a.name} ({formatUSD(a.balance_usd)})
                      {a.master_agent ? ` · ${a.master_agent.username}` : ""}
                    </option>
                  ))}
                </optgroup>
              ) : (
                agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.username} — {a.name} ({formatUSD(a.balance_usd)})
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("cash.amount_usd")}
            </label>
            <input
              data-testid="transfer-amount-input"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
              placeholder="0.00"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("cash.note")}
            </label>
            <input
              data-testid="transfer-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputCls}
              placeholder=""
            />
          </div>
          <button
            data-testid="transfer-submit-button"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
            {t("nav.transfer_funds")}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-900">{t("transfer.recent_title", "Recent transfers")}</div>
          <div className="text-xs text-slate-500">{t("transfer.audit_hint", "Audit trail of all fund movements")}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transfers-table">
            <thead>
              <tr>
                {[t("common.date"), t("common.status"), t("common.amount"), t("wallet.col_balance_after"), t("wallet.col_notes")].map((h) => (
                  <th
                    key={h}
                    className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transfers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500">
                    {t("common.no_results")}
                  </td>
                </tr>
              )}
              {transfers.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-700">{formatDate(r.created_at)}</td>
                  <td className="px-5 py-3">
                    <span
                      className={
                        "inline-flex px-2 py-0.5 rounded-md text-xs font-semibold " +
                        (["TRANSFER_OUT", "WITHDRAWAL_OUT"].includes(r.category)
                          ? "bg-red-50 text-red-700"
                          : "bg-emerald-50 text-emerald-700")
                      }
                    >
                      {{
                        TRANSFER_OUT: t("wallet.cat_transfer_out"),
                        TRANSFER_IN: t("wallet.cat_transfer_in"),
                        WITHDRAWAL_OUT: t("transfer.withdraw_out", "Withdraw out"),
                        WITHDRAWAL_IN: t("transfer.withdraw_in", "Withdraw in"),
                      }[r.category] || r.category}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{formatUSD(r.amount)}</td>
                  <td className="px-5 py-3 text-slate-700">{formatUSD(r.balance_after)}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{r.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
